// Halaman PUBLIK — dibuka pelanggan dari tautan WhatsApp, TANPA login.
// Sengaja TIDAK memakai instance axios `api` dari lib/api.js: interceptor
// permintaannya menempelkan Bearer token dari localStorage kalau ADA
// (mis. perangkat bersama yang sebelumnya dipakai login admin/kasir), dan
// interceptor responsnya bereaksi ke SETIAP 401 di aplikasi (hapus token +
// event logout). Endpoint publik ini tidak butuh auth sama sekali, jadi
// dipakai fetch polos supaya halaman ini benar-benar tidak bergantung sesi
// siapa pun yang kebetulan tersimpan di perangkat. Pola tampilan mengikuti
// CsatSurvey.jsx (acuan halaman publik lain di aplikasi ini).
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Frown, Clock, Printer, Star, CheckCircle2 } from "lucide-react";
import { API } from "../lib/api";
import FakturPrint, { isStruk } from "../components/faktur/FakturPrint";
import StrukPrint from "../components/faktur/StrukPrint";
import LoadingState from "@/components/ui/loading-state";

export default function FakturPublik() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    fetch(`${API}/faktur/publik/${token}`)
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          const err = new Error(body?.detail || "");
          err.status = res.status;
          throw err;
        }
        return body;
      })
      .then((body) => { if (alive) setData(body); })
      .catch((err) => {
        if (!alive) return;
        setStatus(err.status || 0);
        setError(
          err.status
            ? (err.message || "Tautan faktur tidak dapat dibuka. Coba lagi beberapa saat.")
            : "Gagal memuat faktur. Periksa koneksi internet Anda lalu coba lagi."
        );
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const csat = data?.csat_link;
  const bolehNilai = !!(data?.template?.opsi?.sertakan_link_csat && csat?.survey_url && !csat.sudah_diisi && !csat.kedaluwarsa);
  const Komponen = data && isStruk(data.template) ? StrukPrint : FakturPrint;

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="bg-slate-900 px-4 py-4 no-print">
        <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wider">ZOA Group</p>
        <p className="text-white font-semibold text-sm mt-0.5">Faktur Digital</p>
      </div>

      <div className="max-w-2xl mx-auto p-3 md:p-6">
        {loading && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10"
               data-testid="faktur-publik-loading">
            <LoadingState loading jumlahKartu={1} />
          </div>
        )}

        {!loading && error && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10 text-center" data-testid="faktur-publik-error">
            {status === 410 ? (
              <Clock className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            ) : (
              <Frown className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            )}
            <p className="font-semibold text-slate-800 mb-1">
              {status === 410 ? "Tautan sudah kedaluwarsa" : "Faktur tidak ditemukan"}
            </p>
            <p className="text-sm text-slate-500">{error}</p>
          </div>
        )}

        {!loading && !error && data && (
          <>
            <div className="no-print flex flex-col sm:flex-row gap-2 mb-4">
              {bolehNilai && (
                <a href={csat.survey_url} data-testid="faktur-publik-nilai"
                   className="flex-1 h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm inline-flex items-center justify-center gap-2">
                  <Star className="w-5 h-5" /> Nilai Pelayanan Kami
                </a>
              )}
              {csat?.sudah_diisi && (
                <div className="flex-1 h-12 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 font-medium text-sm inline-flex items-center justify-center gap-2"
                     data-testid="faktur-publik-sudah-dinilai">
                  <CheckCircle2 className="w-4 h-4" /> Terima kasih, sudah dinilai
                </div>
              )}
              <button type="button" onClick={() => window.print()} data-testid="faktur-publik-cetak"
                className="flex-1 h-12 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-semibold text-sm inline-flex items-center justify-center gap-2">
                <Printer className="w-5 h-5" /> Cetak / Simpan
              </button>
            </div>

            <div className="overflow-x-auto pb-4">
              <Komponen
                data={data.sale}
                template={data.template}
                publik
                noFaktur={data.no_faktur}
                csatLink={data.csat_link}
                tampilkanTombolCetak={false}
              />
            </div>
          </>
        )}

        <p className="text-center text-xs text-slate-400 mt-6 pb-6">Faktur digital · ZOA Group</p>
      </div>
    </div>
  );
}
